import { useState, useEffect } from 'react'
import {
  Star,
  MessageSquare,
  Calendar,
  TrendingUp,
  Award,
} from 'lucide-react'
import { evaluationsApi } from '../../lib/api/evaluations'

interface EnrichedRating {
  id: number
  score: number
  comment: string | null
  client_name: string | null
  session_title: string | null
  created_at: string
}

interface RatingsData {
  average_rating: number | null
  total_ratings: number
  ratings: EnrichedRating[]
  distribution: number[]
}

export default function InterviewerRatings() {
  const [data, setData] = useState<RatingsData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchRatings = async () => {
      try {
        const result: RatingsData = await evaluationsApi.getMyRatings()
        setData(result)
      } catch (err) {
        console.error('Failed to fetch ratings:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchRatings()
  }, [])

  if (loading) {
    return <div className="loading-screen">Loading ratings...</div>
  }

  const distribution = data?.distribution ?? [0, 0, 0, 0, 0]
  const maxCount = Math.max(...distribution, 1)
  const avgRating = data?.average_rating ?? 0

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">My Ratings & Scorecard</h1>
        <p className="page-subtitle">
          See how clients have rated your interview sessions
        </p>
      </div>

      {/* Summary Cards */}
      <div className="stats-grid" style={{ marginBottom: 32 }}>
        <div className="stat-card" style={{ textAlign: 'center' }}>
          <Award size={32} style={{ color: '#d4af37', marginBottom: 8 }} />
          <div className="value">
            {avgRating > 0 ? avgRating.toFixed(1) : '0.0'}
          </div>
          <div className="label">Average Rating</div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              marginTop: 8,
            }}
          >
            {[1, 2, 3, 4, 5].map((s) => (
              <Star
                key={s}
                size={18}
                fill={s <= Math.round(avgRating) ? '#d4af37' : 'none'}
                stroke={s <= Math.round(avgRating) ? '#d4af37' : '#cbd5e1'}
              />
            ))}
          </div>
        </div>
        <div className="stat-card">
          <div className="icon primary">
            <MessageSquare size={24} />
          </div>
          <div className="value">{data?.total_ratings ?? 0}</div>
          <div className="label">Total Ratings</div>
        </div>
        <div className="stat-card">
          <div className="icon success">
            <TrendingUp size={24} />
          </div>
          <div className="value">
            {data?.ratings?.filter((r) => r.comment).length ?? 0}
          </div>
          <div className="label">With Comments</div>
        </div>
      </div>

      {/* Distribution */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
          Rating Distribution
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[5, 4, 3, 2, 1].map((star) => (
            <div
              key={star}
              style={{ display: 'flex', alignItems: 'center', gap: 12 }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  minWidth: 40,
                  justifyContent: 'flex-end',
                }}
              >
                {star} <Star size={12} fill="#d4af37" stroke="#d4af37" />
              </div>
              <div
                style={{
                  flex: 1,
                  height: 12,
                  background: 'var(--border)',
                  borderRadius: 6,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${(distribution[star - 1] / maxCount) * 100}%`,
                    background: '#d4af37',
                    borderRadius: 6,
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
              <div
                style={{
                  minWidth: 24,
                  fontSize: 14,
                  fontWeight: 600,
                  textAlign: 'center',
                }}
              >
                {distribution[star - 1]}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Client Feedback */}
      <div className="card">
        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
          Client Feedback
        </h3>
        {!data?.ratings || data.ratings.length === 0 ? (
          <div className="empty-state">
            <Star size={48} />
            <h3>No ratings yet</h3>
            <p>
              Complete sessions and clients will be able to rate their
              experience.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {data.ratings.map((r) => (
              <div
                key={r.id}
                style={{
                  padding: 16,
                  background: 'var(--accent-light)',
                  borderRadius: 10,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <div style={{ display: 'flex', gap: 2 }}>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        size={16}
                        fill={s <= Math.round(r.score) ? '#d4af37' : 'none'}
                        stroke={
                          s <= Math.round(r.score) ? '#d4af37' : '#cbd5e1'
                        }
                      />
                    ))}
                  </div>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 12,
                      color: 'var(--text-muted)',
                    }}
                  >
                    <Calendar size={12} />
                    {new Date(r.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>
                {r.session_title && (
                  <div
                    style={{
                      fontSize: 13,
                      color: 'var(--text-muted)',
                      marginBottom: 4,
                    }}
                  >
                    {r.session_title}
                  </div>
                )}
                {r.comment && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      marginTop: 8,
                      padding: 12,
                      background: 'white',
                      borderRadius: 8,
                    }}
                  >
                    <MessageSquare
                      size={14}
                      style={{
                        color: 'var(--text-muted)',
                        marginTop: 2,
                        flexShrink: 0,
                      }}
                    />
                    <p style={{ fontSize: 14, fontStyle: 'italic' }}>
                      "{r.comment}"
                    </p>
                  </div>
                )}
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    marginTop: 8,
                    textAlign: 'right',
                  }}
                >
                  — {r.client_name ?? 'Anonymous'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
